import React, {FormEvent, useEffect, useState} from 'react';
import './App.css';
import AuthPanel from './components/dashboard/AuthPanel';
import {requestDashboard} from './components/dashboard/api';
import Header from './components/dashboard/Header';
import {
  MessagesTab,
  ProfileTab,
  ReservationsTab,
  UsersTab,
} from './components/dashboard/Tabs';
import {
  ReservationDetailModal,
  UserDetailModal,
} from './components/dashboard/Modals';
import Sidebar from './components/dashboard/Sidebar';
import {
  AuthPayload,
  ContactMessage,
  CreateUserPayload,
  DashboardView,
  LoginResponse,
  PaginationResult,
  Reservation,
  ReservationSummary,
  ResetPasswordPayload,
  UpdateMyPasswordPayload,
  User,
  UserRole,
} from './components/dashboard/types';

const DEFAULT_API_BASE = 'https://api.nook.co.tz/api/v1';
const TOKEN_STORAGE_KEY = 'restaurant_dash_token';
const MESSAGES_PAGE_SIZE = 10;

function decodeJwtPayload(token: string): AuthPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    );
    const payload = atob(padded);
    return JSON.parse(payload) as AuthPayload;
  } catch {
    return null;
  }
}

function App() {
  const apiBase = DEFAULT_API_BASE;
  const [activeView, setActiveView] = useState<DashboardView>('reservations');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');

  const [token, setToken] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authRole, setAuthRole] = useState<UserRole | ''>('');

  const [page, setPage] = useState(1);
  const [reservations, setReservations] =
    useState<PaginationResult<Reservation> | null>(null);
  const [summary, setSummary] = useState<ReservationSummary | null>(null);
  const [reservationDrafts, setReservationDrafts] = useState<
    Record<string, Partial<Reservation>>
  >({});
  const [usersPage, setUsersPage] = useState(1);
  const [users, setUsers] = useState<PaginationResult<User> | null>(null);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [myProfile, setMyProfile] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<CreateUserPayload>({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  const [isSubmittingLogin, setIsSubmittingLogin] = useState(false);
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const [isLoadingReservations, setIsLoadingReservations] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showCreateUserPassword, setShowCreateUserPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCalendarPopup, setShowCalendarPopup] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState<
    string | null
  >(null);

  const trendMax = Math.max(
    ...(summary?.trendLast7Days.map((point) => point.totalCount) || [1]),
    1,
  );

  const now = new Date();
  const calendarWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const monthLabel = monthStart.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const firstWeekday = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const calendarDays = [
    ...Array.from({length: firstWeekday}, () => null),
    ...Array.from({length: daysInMonth}, (_, index) => index + 1),
  ];
  const nextHour = new Date(now.getTime() + 60 * 60 * 1000);
  const nextHourReservations = (reservations?.items || [])
    .filter((reservation) => {
      if (!['booked', 'confirmed'].includes(reservation.status)) {
        return false;
      }

      const reservationDate = new Date(
        `${reservation.date}T${reservation.time}`,
      );
      return reservationDate >= now && reservationDate <= nextHour;
    })
    .sort((a, b) => {
      const aDate = new Date(`${a.date}T${a.time}`).getTime();
      const bDate = new Date(`${b.date}T${b.time}`).getTime();
      return aDate - bDate;
    });
  const selectedReservation = (reservations?.items || []).find(
    (reservation) => reservation.id === selectedReservationId,
  );
  const messagesTotalPages = Math.max(
    1,
    Math.ceil(messages.length / MESSAGES_PAGE_SIZE),
  );
  const visibleMessages = messages.slice(
    (messagesPage - 1) * MESSAGES_PAGE_SIZE,
    messagesPage * MESSAGES_PAGE_SIZE,
  );
  const greetingName =
    myProfile?.name?.trim() || authEmail.split('@')[0] || 'there';

  const isAuthenticated = token.trim().length > 0;
  const canAccessDashboard = isAuthenticated && !!authRole;
  const canManageReservations = authRole === 'admin' || authRole === 'manager';
  const canManageUsers = authRole === 'admin';

  const getStatusBadgeClass = (status: Reservation['status']) =>
    `status-badge status-${status}`;

  const getUserMessage = (
    error: unknown,
    fallback = 'Something went wrong. Please try again.',
  ) => {
    if (error instanceof Error && error.message.trim()) {
      return error.message;
    }
    return fallback;
  };

  const loadReservations = async (nextPage: number) => {
    if (!canManageReservations) {
      return;
    }

    setIsLoadingReservations(true);
    try {
      const data = await requestDashboard<PaginationResult<Reservation>>({
        apiBase,
        token,
        path: `/reservations?page=${nextPage}&limit=10`,
        withAuth: true,
      });
      setReservations(data);
      setStatusMessage('');
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load reservations right now. Please try again.',
        ),
      );
    } finally {
      setIsLoadingReservations(false);
    }
  };

  const loadSummary = async () => {
    if (!canManageReservations) {
      return;
    }

    try {
      const data = await requestDashboard<ReservationSummary>({
        apiBase,
        token,
        path: '/reservations/summary',
        withAuth: true,
      });
      setSummary(data);
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load summary right now. Please try again.',
        ),
      );
    }
  };

  const loadUsers = async (nextPage: number) => {
    if (!canManageUsers) {
      return;
    }

    setIsLoadingUsers(true);
    try {
      const data = await requestDashboard<PaginationResult<User>>({
        apiBase,
        token,
        path: `/users/paginated?page=${nextPage}&limit=10`,
        withAuth: true,
      });
      setUsers(data);
      setStatusMessage('');
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load users right now. Please try again.',
        ),
      );
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const loadMyProfile = async () => {
    if (!canAccessDashboard) {
      return;
    }

    try {
      const data = await requestDashboard<User>({
        apiBase,
        token,
        path: '/users/me',
        withAuth: true,
      });
      setMyProfile(data);
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load your profile right now. Please try again.',
        ),
      );
    }
  };

  const loadMessages = async () => {
    if (!canManageReservations) {
      return;
    }

    setIsLoadingMessages(true);
    try {
      const data = await requestDashboard<ContactMessage[]>({
        apiBase,
        token,
        path: '/messages',
        withAuth: true,
      });
      setMessages(data);
      setMessagesPage(1);
      setStatusMessage('');
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load messages right now. Please try again.',
        ),
      );
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const loadUserDetail = async (userId: string) => {
    if (!canManageUsers) {
      return;
    }

    try {
      const data = await requestDashboard<User>({
        apiBase,
        token,
        path: `/users/${userId}`,
        withAuth: true,
      });
      setSelectedUser(data);
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to load user details right now. Please try again.',
        ),
      );
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      return;
    }

    const payload = decodeJwtPayload(storedToken);
    if (
      !payload ||
      (payload.role !== 'admin' &&
        payload.role !== 'manager' &&
        payload.role !== 'user')
    ) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      return;
    }

    setToken(storedToken);
    setAuthRole(payload.role);
    setAuthEmail(payload.email || payload.role);
    setActiveView(payload.role === 'user' ? 'profile' : 'reservations');
  }, []);

  useEffect(() => {
    if (canManageReservations && activeView === 'reservations') {
      loadReservations(page);
      loadSummary();
    }
  }, [page, canManageReservations, activeView]);

  useEffect(() => {
    if (canManageUsers && activeView === 'users') {
      loadUsers(usersPage);
    }
  }, [usersPage, canManageUsers, activeView]);

  useEffect(() => {
    if (canManageReservations && activeView === 'messages') {
      loadMessages();
    }
  }, [canManageReservations, activeView]);

  useEffect(() => {
    if (messagesPage > messagesTotalPages) {
      setMessagesPage(messagesTotalPages);
    }
  }, [messagesPage, messagesTotalPages]);

  useEffect(() => {
    if (activeView === 'users' && !canManageUsers) {
      setActiveView('profile');
      return;
    }

    if (activeView === 'reservations' && !canManageReservations) {
      setActiveView('profile');
      return;
    }

    if (activeView === 'messages' && !canManageReservations) {
      setActiveView('profile');
    }
  }, [activeView, canManageUsers, canManageReservations]);

  useEffect(() => {
    if (canAccessDashboard) {
      loadMyProfile();
    }
  }, [canAccessDashboard]);

  useEffect(() => {
    if (!showCalendarPopup && !selectedReservationId && !selectedUser) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showCalendarPopup) {
          setShowCalendarPopup(false);
          return;
        }
        if (selectedUser) {
          setSelectedUser(null);
          return;
        }
        setSelectedReservationId(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showCalendarPopup, selectedReservationId, selectedUser]);

  const handleLogin = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmittingLogin(true);
    setStatusMessage('');

    try {
      const data = await requestDashboard<LoginResponse>({
        apiBase,
        path: '/auth/login',
        init: {
          method: 'POST',
          body: JSON.stringify({email, password}),
        },
      });

      const payload = decodeJwtPayload(data.accessToken);
      if (
        !payload ||
        (payload.role !== 'admin' &&
          payload.role !== 'manager' &&
          payload.role !== 'user')
      ) {
        setToken('');
        setAuthRole('');
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setStatusMessage(
          'Access denied: only staff users can access this dashboard.',
        );
        return;
      }

      setToken(data.accessToken);
      setAuthRole(payload.role);
      setAuthEmail(payload.email || email);
      localStorage.setItem(TOKEN_STORAGE_KEY, data.accessToken);
      setActiveView(payload.role === 'user' ? 'profile' : 'reservations');

      setPage(1);
      setUsersPage(1);
      await loadMyProfile();
      if (payload.role === 'admin' || payload.role === 'manager') {
        await loadReservations(1);
        await loadSummary();
      }
    } catch (error) {
      setStatusMessage(
        getUserMessage(error, 'Sign in failed. Please try again.'),
      );
    } finally {
      setIsSubmittingLogin(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setAuthRole('');
    setAuthEmail('');
    setMyProfile(null);
    setEmail('');
    setPassword('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmNewPassword(false);
    setActiveView('profile');
    setReservations(null);
    setSummary(null);
    setUsers(null);
    setMessagesPage(1);
    setMessages([]);
    setSelectedUser(null);
    setReservationDrafts({});
    setStatusMessage('You have been logged out.');
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  };

  const handleResetPassword = async (event: FormEvent) => {
    event.preventDefault();

    if (resetPassword !== resetPasswordConfirm) {
      setStatusMessage('Passwords do not match.');
      return;
    }

    if (resetPassword.length < 8) {
      setStatusMessage('Password must be at least 8 characters long.');
      return;
    }

    setIsSubmittingReset(true);
    setStatusMessage('');

    try {
      await requestDashboard<void>({
        apiBase,
        path: '/auth/reset-password',
        init: {
          method: 'POST',
          body: JSON.stringify({
            email: resetEmail,
            newPassword: resetPassword,
          } as ResetPasswordPayload),
        },
      });

      setShowResetForm(false);
      setResetEmail('');
      setResetPassword('');
      setResetPasswordConfirm('');
      setStatusMessage('Password reset submitted successfully.');
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to submit password reset right now. Please try again.',
        ),
      );
    } finally {
      setIsSubmittingReset(false);
    }
  };

  const updateReservationDraft = (
    id: string,
    key: keyof Reservation,
    value: string,
  ) => {
    setReservationDrafts((previous) => ({
      ...previous,
      [id]: {
        ...previous[id],
        [key]: key === 'numberOfGuests' ? Number(value) : value,
      },
    }));
  };

  const handleSaveReservation = async (id: string) => {
    const payload = reservationDrafts[id];
    if (!payload || Object.keys(payload).length === 0) {
      setStatusMessage('No changes to save.');
      return false;
    }

    try {
      await requestDashboard({
        apiBase,
        token,
        path: `/reservations/${id}`,
        init: {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        withAuth: true,
      });

      setReservationDrafts((previous) => {
        const copy = {...previous};
        delete copy[id];
        return copy;
      });

      setStatusMessage('Reservation updated successfully.');
      await loadReservations(page);
      await loadSummary();
      return true;
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to update this reservation right now. Please try again.',
        ),
      );
      return false;
    }
  };

  const handleCreateUser = async (event: FormEvent) => {
    event.preventDefault();

    if (!canManageUsers) {
      setStatusMessage('Only admin users can manage team accounts.');
      return;
    }

    try {
      await requestDashboard({
        apiBase,
        token,
        path: '/users',
        init: {
          method: 'POST',
          body: JSON.stringify(newUser),
        },
        withAuth: true,
      });

      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'user',
      });
      setStatusMessage('User added successfully.');
      await loadUsers(usersPage);
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to add user right now. Please try again.',
        ),
      );
    }
  };

  const handleUpdateMyPassword = async (event: FormEvent) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setStatusMessage('Please complete all password fields.');
      return;
    }

    if (newPassword.length < 8) {
      setStatusMessage('New password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setStatusMessage('New password and confirm password do not match.');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await requestDashboard<{message: string}>({
        apiBase,
        token,
        path: '/users/me/password',
        init: {
          method: 'PATCH',
          body: JSON.stringify({
            currentPassword,
            newPassword,
          } as UpdateMyPasswordPayload),
        },
        withAuth: true,
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
      setStatusMessage('Password updated successfully.');
    } catch (error) {
      setStatusMessage(
        getUserMessage(
          error,
          'Unable to update your password right now. Please try again.',
        ),
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!canAccessDashboard) {
    return (
      <AuthPanel
        showResetForm={showResetForm}
        statusMessage={statusMessage}
        resetEmail={resetEmail}
        resetPassword={resetPassword}
        resetPasswordConfirm={resetPasswordConfirm}
        email={email}
        password={password}
        isSubmittingReset={isSubmittingReset}
        isSubmittingLogin={isSubmittingLogin}
        onResetEmailChange={setResetEmail}
        onResetPasswordChange={setResetPassword}
        onResetPasswordConfirmChange={setResetPasswordConfirm}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onResetFormSubmit={handleResetPassword}
        onLoginFormSubmit={handleLogin}
        onShowResetForm={() => {
          setShowResetForm(true);
          setStatusMessage('');
        }}
        onShowLoginForm={() => {
          setShowResetForm(false);
          setStatusMessage('');
        }}
      />
    );
  }

  return (
    <main className='dashboard-page'>
      <div className='dashboard-canvas'>
        <Sidebar
          activeView={activeView}
          canManageReservations={canManageReservations}
          canManageUsers={canManageUsers}
          onChangeView={setActiveView}
          onLogout={handleLogout}
        />

        <section className='board item-start justify-start'>
          <Header
            activeView={activeView}
            greetingName={greetingName}
            authEmail={authEmail}
            now={now}
            isLoading={
              isLoadingReservations || isLoadingUsers || isLoadingMessages
            }
            onToggleCalendar={() =>
              setShowCalendarPopup((previous) => !previous)
            }
            onRefresh={() => {
              if (activeView === 'profile') {
                loadMyProfile();
                return;
              }
              if (activeView === 'reservations') {
                loadReservations(page);
                loadSummary();
                return;
              }
              if (activeView === 'messages') {
                loadMessages();
                return;
              }
              if (canManageUsers) {
                loadUsers(usersPage);
              }
            }}
          />

          {statusMessage ? (
            <p className='status success'>{statusMessage}</p>
          ) : null}

          <div className='board-content'>
            {activeView === 'profile' ? (
              <ProfileTab
                myProfile={myProfile}
                authEmail={authEmail}
                authRole={authRole}
                currentPassword={currentPassword}
                newPassword={newPassword}
                confirmNewPassword={confirmNewPassword}
                showCurrentPassword={showCurrentPassword}
                showNewPassword={showNewPassword}
                showConfirmNewPassword={showConfirmNewPassword}
                isUpdatingPassword={isUpdatingPassword}
                setCurrentPassword={setCurrentPassword}
                setNewPassword={setNewPassword}
                setConfirmNewPassword={setConfirmNewPassword}
                toggleShowCurrentPassword={() =>
                  setShowCurrentPassword((previous) => !previous)
                }
                toggleShowNewPassword={() =>
                  setShowNewPassword((previous) => !previous)
                }
                toggleShowConfirmNewPassword={() =>
                  setShowConfirmNewPassword((previous) => !previous)
                }
                onSubmit={handleUpdateMyPassword}
              />
            ) : activeView === 'reservations' ? (
              <ReservationsTab
                summary={summary}
                trendMax={trendMax}
                nextHourReservations={nextHourReservations}
                reservations={reservations}
                page={page}
                getStatusBadgeClass={getStatusBadgeClass}
                onSelectReservation={setSelectedReservationId}
                onPageChange={setPage}
              />
            ) : activeView === 'messages' ? (
              <MessagesTab
                visibleMessages={visibleMessages}
                messagesPage={messagesPage}
                messagesTotalPages={messagesTotalPages}
                setMessagesPage={setMessagesPage}
              />
            ) : (
              <UsersTab
                newUser={newUser}
                setNewUser={setNewUser}
                showCreateUserPassword={showCreateUserPassword}
                toggleCreateUserPassword={() =>
                  setShowCreateUserPassword((previous) => !previous)
                }
                users={users}
                usersPage={usersPage}
                setUsersPage={setUsersPage}
                handleCreateUser={handleCreateUser}
                loadUserDetail={loadUserDetail}
              />
            )}
          </div>
        </section>
      </div>

      <ReservationDetailModal
        selectedReservation={selectedReservation || null}
        reservationDrafts={reservationDrafts}
        getStatusBadgeClass={getStatusBadgeClass}
        updateReservationDraft={updateReservationDraft}
        onClose={() => setSelectedReservationId(null)}
        onSave={handleSaveReservation}
      />

      <UserDetailModal
        selectedUser={selectedUser}
        onClose={() => setSelectedUser(null)}
      />

      {showCalendarPopup && (
        <div
          className='calendar-modal-overlay'
          onClick={() => setShowCalendarPopup(false)}>
          <div
            className='calendar-popover'
            role='dialog'
            aria-modal='true'
            aria-label='Calendar'
            onClick={(event) => event.stopPropagation()}>
            <div className='calendar-header'>
              <p className='calendar-title'>{monthLabel}</p>
              <button
                type='button'
                className='calendar-close'
                onClick={() => setShowCalendarPopup(false)}>
                Close
              </button>
            </div>

            <div className='calendar-weekdays'>
              {calendarWeekdays.map((day) => (
                <span
                  key={day}
                  className='calendar-weekday'>
                  {day}
                </span>
              ))}
            </div>
            <div className='calendar-grid'>
              {calendarDays.map((day, index) => {
                const isToday = day === now.getDate();
                return (
                  <span
                    key={`${day}-${index}`}
                    className={`calendar-day ${!day ? 'empty' : ''} ${
                      isToday ? 'today' : ''
                    }`}>
                    {day ?? ''}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default App;
